import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useMode } from '../contexts/ModeContext';
import {
  ArrowRight, Monitor, ShoppingCart, Code, Check,
  Calendar, Phone, Zap, Shield, Users,
  Sparkles, MessageSquare, Globe, Lock, Package,
  DollarSign, Server, Layers, Search
} from 'lucide-react';
import axios from 'axios';
import SaaSPreview from '../components/SaaSPreview';
import { BackgroundShapes, HieroglyphText, CodeText } from '../components/TextAnimations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Composant point animé - SIMPLIFIÉ
const PulseDot = ({ className = "" }) => {
  const { isLabs } = useMode();
  return (
    <span
      className={`inline-block w-2.5 h-2.5 rounded-full ${className} animate-pulse`}
      style={{ background: isLabs ? '#E07A5F' : '#38BDF8' }}
    />
  );
};

// =============================================
// LABS AGENCY - Sections
// =============================================

const LabsHero = () => {
  const { t } = useTranslation();
  return (
    <section className="min-h-screen flex items-center pt-36 sm:pt-32 pb-20 px-4 relative overflow-hidden">
      <BackgroundShapes />

      <div className="max-w-7xl mx-auto w-full relative z-10">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-50 border border-yellow-200 text-sm font-semibold mb-8 text-yellow-700"
            >
              <Sparkles className="w-4 h-4" /> {t('home.badge')}
            </motion.div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.1] mb-6">
              <HieroglyphText
                text="Labs Agency:"
                className="text-yellow-500"
                delay={300}
              />
              <br />
              <HieroglyphText
                text={t('home.hero_sub1')}
                className="text-gray-900"
                delay={600}
              />
              <br />
              <HieroglyphText
                text={t('home.hero_sub2')}
                className="text-gray-900"
                delay={900}
              />
            </h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
              className="text-lg text-gray-600 mb-8 max-w-xl leading-relaxed"
            >
              {t('home.hero_desc')}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4 }}
              className="flex flex-wrap gap-4"
            >
              <motion.a
                href="#services"
                className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-400 hover:bg-yellow-500 text-black font-bold rounded-full transition-all"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <Zap className="w-4 h-4" /> {t('home.discover_services')}
              </motion.a>
              <motion.a
                href="https://calendly.com/labsagency"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 border-2 border-pink-400 text-pink-500 font-bold rounded-full hover:bg-pink-50 transition-all"
                whileHover={{ scale: 1.02 }}
              >
                <MessageSquare className="w-4 h-4" /> {t('home.talk_expert')}
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const LabsAgencyPresentation = () => {
  const { t } = useTranslation();
  const cards = [
    { icon: Sparkles, titleKey: 'home.creativity', descKey: 'home.creativity_desc' },
    { icon: Code, titleKey: 'home.technical', descKey: 'home.technical_desc' },
    { icon: Users, titleKey: 'home.support', descKey: 'home.support_desc' },
  ];
  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-black mb-4">
            <span className="gradient-labs">{t('home.who_we_are')}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('home.who_we_are_desc')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {cards.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="card text-center"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center">
                <item.icon className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-xl mb-2">{t(item.titleKey)}</h3>
              <p className="text-gray-600">{t(item.descKey)}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const LabsWebServices = () => {
  const { t } = useTranslation();
  const services = [
    {
      icon: Globe,
      name: t('packs.vitrine_name'),
      subtitle: t('packs.vitrine_sub'),
      price: "699€",
      features: [
        { icon: Search, text: t('packs.vitrine_f1') },
        { icon: Zap, text: t('packs.vitrine_f2') },
        { icon: Server, text: t('packs.vitrine_f3') },
        { icon: ShoppingCart, text: t('packs.vitrine_f4') },
      ]
    },
    {
      icon: ShoppingCart,
      name: t('packs.ecom_name'),
      subtitle: t('packs.ecom_sub'),
      price: "299€",
      badge: t('packs.most_popular'),
      features: [
        { icon: Package, text: t('packs.ecom_f1') },
        { icon: Lock, text: t('packs.ecom_f2') },
        { icon: DollarSign, text: t('packs.ecom_f3') },
        { icon: Globe, text: t('packs.ecom_f4') },
      ]
    },
    {
      icon: Code,
      name: t('packs.app_name'),
      subtitle: t('packs.app_sub'),
      price: "2500€",
      features: [
        { icon: Layers, text: t('packs.app_f1') },
        { icon: Server, text: t('packs.app_f2') },
        { icon: Code, text: t('packs.app_f3') },
      ]
    }
  ];

  return (
    <section id="services" className="py-20 px-4 bg-gray-50 relative overflow-hidden">
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 rounded-full bg-orange-200/20 blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-pink-200/20 blur-3xl" />
      </div>
      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl font-black mb-4 gradient-labs tracking-tight">
            {t('home.services_title')}
          </h2>
          <p className="text-base sm:text-lg text-gray-600 font-medium">{t('home.services_subtitle')}</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {services.map((svc, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.4 }}
              className="relative"
            >
              <motion.div
                className="h-full p-8 rounded-2xl bg-white relative overflow-hidden border border-gray-200 shadow-sm flex flex-col"
                whileHover={{ y: -8, borderColor: 'rgba(249,115,22,0.4)', boxShadow: '0 20px 40px rgba(0,0,0,0.08)' }}
                transition={{ duration: 0.3 }}
              >
                {svc.badge && (
                  <div className="absolute top-6 right-6">
                    <span className="px-3 py-1 bg-orange-100 text-orange-600 text-xs font-semibold rounded-full border border-orange-200">
                      {svc.badge}
                    </span>
                  </div>
                )}

                <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-4 border border-orange-100">
                  <svc.icon className="w-6 h-6 text-orange-500" />
                </div>

                <h3 className="text-xl font-black text-gray-900 mb-1 tracking-tight">{svc.name}</h3>
                <p className="text-gray-400 text-xs mb-4 font-medium">{svc.subtitle}</p>

                <div className="mb-6">
                  <span className="text-3xl font-black text-gray-900">{t('packs.from')} {svc.price}</span>
                </div>

                <div className="space-y-2.5 mb-8 flex-1">
                  {svc.features.map((f, j) => (
                    <div key={j} className="flex items-center gap-3">
                      <div className="w-7 h-7 bg-orange-50 rounded-lg flex items-center justify-center flex-shrink-0 border border-orange-100">
                        <f.icon className="w-3.5 h-3.5 text-orange-500" />
                      </div>
                      <span className="text-gray-600 text-sm">{f.text}</span>
                    </div>
                  ))}
                </div>

                <a
                  href="#calendly"
                  className="w-full py-3.5 bg-gradient-to-r from-orange-400 to-pink-400 text-white font-bold rounded-xl inline-flex items-center justify-center gap-2 group hover:from-orange-500 hover:to-pink-500 transition-all shadow-md text-sm"
                >
                  <span>{t('packs.cta')}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const LabsCallUpShowcase = () => {
  const { t } = useTranslation();
  const featureKeys = ['home.callup_f1', 'home.callup_f2', 'home.callup_f3', 'home.callup_f4'];
  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-1 bg-sky-100 text-sky-600 rounded-full text-sm font-semibold mb-4">
              {t('home.our_saas')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-black mb-4">
              <span className="gradient-callup">Call Up</span>
            </h2>
            <ul className="space-y-3 mb-6">
              {featureKeys.map((key, i) => (
                <li key={i} className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-sky-500 flex-shrink-0" />
                  <span className="text-gray-700">{t(key)}</span>
                </li>
              ))}
            </ul>
            <a href="#calendly" className="btn-primary inline-flex items-center gap-2">
              {t('home.discover_callup')} <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="bg-gradient-to-br from-sky-100 to-purple-100 rounded-3xl p-8 shadow-2xl">
              <SaaSPreview />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Labs5StepProcess = () => {
  const { t } = useTranslation();
  const steps = [
    { titleKey: 'home.step1', descKey: 'home.step1_desc' },
    { titleKey: 'home.step2', descKey: 'home.step2_desc' },
    { titleKey: 'home.step3', descKey: 'home.step3_desc' },
    { titleKey: 'home.step4', descKey: 'home.step4_desc' },
    { titleKey: 'home.step5', descKey: 'home.step5_desc' },
  ];

  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-black mb-2">
            <span className="gradient-labs">{t('home.process_title')}</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-5 gap-4">
          {steps.map((step, i) => (
            <div key={i} className="card text-center relative">
              <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center text-white font-bold">
                {i + 1}
              </div>
              <h4 className="font-bold mb-1">{t(step.titleKey)}</h4>
              <p className="text-xs text-gray-600">{t(step.descKey)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const LabsContact = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, form);
      setStatus('success');
      setForm({ name: '', email: '', message: '' });
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-20 px-4 bg-white">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-black mb-4">
            <span className="gradient-labs">{t('home.contact_title')}</span>
          </h2>
          <p className="text-lg text-gray-600">
            {t('home.contact_subtitle')}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <form onSubmit={handleSubmit} className="space-y-4 max-w-xl mx-auto">
            <input
              type="text"
              placeholder={t('home.form_name')}
              required
              className="input-field"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <input
              type="email"
              placeholder={t('home.form_email')}
              required
              className="input-field"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <textarea
              placeholder={t('home.form_project')}
              required
              rows={5}
              className="input-field resize-none"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />

            {status === 'success' && <p className="text-green-600 text-sm">{t('home.form_success')}</p>}
            {status === 'error' && <p className="text-red-600 text-sm">{t('home.form_error')}</p>}

            <button
              type="submit"
              disabled={status === 'loading'}
              className="btn-primary w-full"
            >
              {status === 'loading' ? t('home.form_sending') : t('home.form_send')}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

const LabsCalendly = () => {
  const { t } = useTranslation();
  return (
    <section id="calendly" className="py-20 px-4 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Calendar className="w-8 h-8 text-orange-500" />
            <h2 className="text-3xl sm:text-4xl font-black">
              <span className="gradient-labs">{t('home.book_call')}</span>
            </h2>
          </div>
          <p className="text-lg text-gray-600">
            {t('home.book_desc')}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl p-8 shadow-xl border border-orange-100"
        >
          <iframe
            src="https://calendly.com/labsagency?hide_gdpr_banner=1&background_color=ffffff&text_color=1e293b&primary_color=f97316"
            width="100%"
            height="700"
            frameBorder="0"
            title="Calendly"
            className="rounded-lg"
          />
        </motion.div>
      </div>
    </section>
  );
};

// =============================================
// CALL UP - Sections
// =============================================

const CallUpHero = () => {
  const { t } = useTranslation();
  return (
    <section className="min-h-screen flex items-center pt-36 sm:pt-32 pb-20 px-4 relative overflow-hidden">
      <motion.div
        className="absolute top-20 left-10 w-[500px] h-[500px] rounded-full bg-sky-500/10 blur-[120px]"
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 12, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-purple-500/10 blur-[120px]"
        animate={{ scale: [1, 1.3, 1] }}
        transition={{ duration: 15, repeat: Infinity }}
      />

      <div className="max-w-6xl mx-auto w-full relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-400 text-sm font-semibold mb-8"
            >
              <PulseDot /> {t('home.callup_badge')}
            </motion.div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.1] mb-6">
              <CodeText
                text="Call Up"
                className="gradient-callup"
                delay={300}
              />
              <br />
              <CodeText
                text={t('home.callup_hero_sub1')}
                className="text-white"
                delay={600}
              />
              <br />
              <CodeText
                text={t('home.callup_hero_sub2')}
                className="text-white"
                delay={900}
              />
            </h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
              className="text-lg text-gray-400 mb-6 max-w-md"
            >
              {t('home.callup_hero_desc')}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4 }}
              className="flex flex-wrap gap-4"
            >
              <motion.a href="#pricing" className="btn-primary" whileHover={{ scale: 1.03 }}>
                {t('home.see_pricing')} <ArrowRight className="w-4 h-4" />
              </motion.a>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="relative"
          >
            <SaaSPreview />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Section Trial 1€
const TrialSection = () => {
  const { isLabs } = useMode();
  const [loading, setLoading] = useState(false);

  const handleTrial = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/trial/checkout`);
      window.location.href = res.data.checkout_url;
    } catch (e) {
      setLoading(false);
      alert("Erreur lors de la création du paiement");
    }
  };

  if (isLabs) return (
    <section id="trial" className="py-20 px-4 relative overflow-hidden bg-gradient-to-br from-orange-50 to-pink-50">
      <div className="max-w-3xl mx-auto text-center">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>

          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-orange-100 border border-orange-200 rounded-full text-orange-600 text-sm font-semibold mb-6">
            <Zap className="w-4 h-4" /> Essai sans engagement
          </span>

          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 leading-tight">
            Testez CallUp IA<br />
            <span className="bg-gradient-to-r from-orange-400 to-pink-500 bg-clip-text text-transparent">pour 1€ seulement</span>
          </h2>

          <p className="text-gray-500 text-lg mb-10">
            Payez 1€, entrez votre numéro, et notre agent IA vous rappelle en direct.<br />
            Vivez l'expérience complète en moins de 2 minutes.
          </p>

          <div className="grid grid-cols-3 gap-4 mb-10 max-w-xl mx-auto">
            {[
              { icon: Phone, text: "1 vrai appel IA", sub: "sur votre téléphone" },
              { icon: Zap, text: "Immédiat", sub: "moins de 30 secondes" },
              { icon: Shield, text: "Sans abonnement", sub: "paiement unique" }
            ].map((item, i) => (
              <div key={i} className="bg-white border border-orange-100 rounded-xl p-4 shadow-sm">
                <item.icon className="w-6 h-6 text-orange-400 mx-auto mb-2" />
                <p className="text-gray-800 font-bold text-sm">{item.text}</p>
                <p className="text-gray-400 text-xs">{item.sub}</p>
              </div>
            ))}
          </div>

          <div className="max-w-md mx-auto">
            <SwipeToUnlock label="Glissez pour tester à 1€" redirectLabel="Redirection…" onUnlock={handleTrial} />
          </div>

          <p className="text-gray-400 text-xs mt-4">Paiement sécurisé par Stripe · CB / Apple Pay / Google Pay</p>
        </motion.div>
      </div>
    </section>
  );

  // CallUp dark mode
  return (
    <section id="trial" className="py-24 px-4 relative overflow-hidden bg-[#050510]">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-3xl" />
      </div>
      <div className="max-w-2xl mx-auto relative z-10 text-center">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-full text-purple-300 text-sm font-semibold mb-6">
            <Zap className="w-4 h-4" /> Essai sans engagement
          </span>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight">
            Testez CallUp IA<br />
            <span className="bg-gradient-to-r from-sky-400 to-purple-500 bg-clip-text text-transparent">pour 1€ seulement</span>
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            Payez 1€, entrez votre numéro, et notre agent IA vous rappelle en direct.<br />
            Vivez l'expérience complète en moins de 2 minutes.
          </p>
          <div className="grid grid-cols-3 gap-4 mb-10">
            {[
              { icon: Phone, text: "1 vrai appel IA", sub: "sur votre téléphone" },
              { icon: Zap, text: "Immédiat", sub: "moins de 30 secondes" },
              { icon: Shield, text: "Sans abonnement", sub: "paiement unique" }
            ].map((item, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
                <item.icon className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                <p className="text-white font-bold text-sm">{item.text}</p>
                <p className="text-gray-500 text-xs">{item.sub}</p>
              </div>
            ))}
          </div>
          <div className="max-w-md mx-auto">
            <SwipeToUnlock label="Glissez pour tester à 1€" redirectLabel="Redirection…" onUnlock={handleTrial} />
          </div>
          <p className="text-gray-600 text-xs mt-4">Paiement sécurisé par Stripe · CB / Apple Pay / Google Pay</p>
        </motion.div>
      </div>
    </section>
  );
};

// =============================================
// CALL UP - Section Test 1€ avec swipe-to-unlock
// =============================================

const SwipeToUnlock = ({ label, redirectLabel, onUnlock }) => {
  const { t } = useTranslation();
  const resolvedLabel = label ?? t('unlock.swipeLabel');
  const resolvedRedirectLabel = redirectLabel ?? t('unlock.swipeRedirect');
  const constraintsRef = React.useRef(null);
  const x = useMotionValue(0);
  const [unlocked, setUnlocked] = useState(false);
  const [maxX, setMaxX] = useState(0);

  // Recompute drag range based on container width
  useEffect(() => {
    const update = () => {
      if (constraintsRef.current) {
        const width = constraintsRef.current.offsetWidth;
        setMaxX(width - 64); // 64 = handle size + padding
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const trackOpacity = useTransform(x, [0, maxX || 1], [1, 0]);
  const fillWidth = useTransform(x, [0, maxX || 1], ['0%', '100%']);

  const handleDragEnd = () => {
    const current = x.get();
    if (current >= maxX * 0.85) {
      animate(x, maxX, { type: 'spring', stiffness: 400, damping: 30 });
      setUnlocked(true);
      setTimeout(() => {
        if (onUnlock) onUnlock();
      }, 350);
    } else {
      animate(x, 0, { type: 'spring', stiffness: 400, damping: 30 });
    }
  };

  return (
    <div
      ref={constraintsRef}
      className="relative w-full h-16 rounded-full bg-white/5 border border-white/15 overflow-hidden select-none"
    >
      {/* Gradient fill behind handle */}
      <motion.div
        className="absolute inset-y-0 left-0 bg-gradient-to-r from-cyan-500/30 via-blue-500/30 to-violet-500/30"
        style={{ width: fillWidth }}
      />

      {/* Sliding text */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{ opacity: trackOpacity }}
      >
        <span className="text-white/80 font-semibold text-sm tracking-wide ml-10">
          {unlocked ? resolvedRedirectLabel : resolvedLabel}
          {!unlocked && <span className="ml-2 text-white/50">›››</span>}
        </span>
      </motion.div>

      {/* Drag handle */}
      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: maxX }}
        dragElastic={0}
        dragMomentum={false}
        onDragEnd={handleDragEnd}
        style={{ x }}
        whileTap={{ scale: 0.95 }}
        className="absolute top-1 left-1 w-14 h-14 rounded-full bg-gradient-to-br from-cyan-400 via-blue-500 to-violet-600 flex items-center justify-center shadow-lg shadow-cyan-500/40 cursor-grab active:cursor-grabbing z-10"
      >
        {unlocked ? (
          <Check className="w-6 h-6 text-white" strokeWidth={3} />
        ) : (
          <Lock className="w-6 h-6 text-white" />
        )}
      </motion.div>
    </div>
  );
};

const CallUpUnlockSection = () => {
  const { t } = useTranslation();
  const unlockFeatures = [
    { icon: Phone, label: t('unlock.feature1'), color: 'text-cyan-400', bg: 'bg-cyan-500/20' },
    { icon: Check, label: t('unlock.feature2'), color: 'text-gray-200', bg: 'bg-white/10' },
    { icon: Sparkles, label: t('unlock.feature3'), color: 'text-violet-300', bg: 'bg-violet-500/25' },
  ];

  const handleUnlock = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          price_id: 'trial_1eur',
          success_url: `${window.location.origin}/dashboard?trial=success`,
          cancel_url: `${window.location.origin}/?trial=cancelled`,
          mode: 'payment',
        }),
      });
      const data = await response.json();
      if (data && data.url) {
        window.location.href = data.url;
        return;
      }
    } catch (e) {
      // fallback to pricing anchor
    }
    window.location.href = '#pricing';
  };

  return (
    <section id="essayer" className="py-24 px-4 relative overflow-hidden">
      {/* Background orbs */}
      <motion.div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-cyan-500/10 blur-[120px]"
        animate={{ scale: [1, 1.15, 1] }}
        transition={{ duration: 12, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-0 right-10 w-[400px] h-[400px] rounded-full bg-violet-500/10 blur-[120px]"
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 14, repeat: Infinity }}
      />

      <div className="max-w-xl mx-auto relative z-10 text-center">
        {/* Heading */}
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-4xl sm:text-5xl md:text-6xl font-black mb-5 leading-[1.05] tracking-tight"
        >
          <span className="text-white">{t('unlock.titleLine1')}<br />{t('unlock.titleLine2')} </span>
          <span className="gradient-callup">{t('unlock.titleHighlight')}</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-gray-400 text-base sm:text-lg mb-10 max-w-md mx-auto leading-relaxed"
        >
          {t('unlock.subtitle')}
        </motion.p>

        {/* Decorative locked status pill */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="inline-flex items-center gap-3 px-7 py-3 rounded-full border border-cyan-400/50 bg-cyan-500/5 mb-10"
        >
          <Lock className="w-4 h-4 text-cyan-300" />
          <span className="gradient-callup font-bold text-base">{t('unlock.lockedPill')}</span>
        </motion.div>

        {/* What you unlock card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="bg-white/5 border border-white/10 rounded-3xl p-6 sm:p-7 mb-10 text-left backdrop-blur-sm"
        >
          <div className="flex items-center gap-3 mb-6">
            <Sparkles className="w-5 h-5 text-violet-300" />
            <h3 className="text-white font-bold text-lg sm:text-xl">{t('unlock.cardTitle')}</h3>
          </div>
          <ul className="space-y-4">
            {unlockFeatures.map((feature, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.35 + i * 0.1 }}
                className="flex items-center gap-4"
              >
                <div className={`w-11 h-11 rounded-xl ${feature.bg} flex items-center justify-center flex-shrink-0`}>
                  <feature.icon className={`w-5 h-5 ${feature.color}`} strokeWidth={feature.icon === Check ? 3 : 2} />
                </div>
                <span className="text-white font-medium text-base">{feature.label}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>

        {/* Working swipe-to-unlock */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="mb-4"
        >
          <SwipeToUnlock onUnlock={handleUnlock} />
        </motion.div>

        {/* Trust line */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.7 }}
          className="text-gray-500 text-sm"
        >
          {t('unlock.trust')}
        </motion.p>
      </div>
    </section>
  );
};

// Export all sections
export { LabsHero, LabsAgencyPresentation, LabsWebServices, LabsCallUpShowcase, Labs5StepProcess, LabsContact, LabsCalendly, CallUpHero, CallUpUnlockSection, PulseDot, TrialSection };
